import { useState, useMemo, Fragment, useCallback, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TablePagination,
    FormControl,
    Select,
    MenuItem,
    Typography,
    TableSortLabel,
  } from '@mui/material';
  import SearchIcon from '@mui/icons-material/Search';
  import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
  import { useDateSort } from '../../../../../hooks/useDateSort';
  import TableActionsMenu from '../../../../../components/common/TableActionsMenu';
  import { 
    AdcashAvatyeDetailMetrics,
    AdcashAvatyeDetailMetricsByPlacementWithMediationDailyStats
  } from '../../../../../types/adcash.types';
  import AdcashAvatyeDetailMetricsChart from '../chart/AdcashAvatyeDetailMetricsChart';
  
  type Props = {
    data: AdcashAvatyeDetailMetrics;
    selectedApp: string | null;
    exchangeRate: number;
    onExchangeRateChange: (rate: number) => void;
  };

  type DateSummary = {
    date: string;
    totalRevenue: number;
    totalReq: number;
    totalRes: number;
    totalImp: number;
    totalClk: number;
    mediations: AdcashAvatyeDetailMetricsByPlacementWithMediationDailyStats[];
  };

  export default function AdcashAvatyeDetailMetricsTable({ data, selectedApp, exchangeRate, onExchangeRateChange }: Props) {
    const { dailyStats } = data;
    const rowsPerPage = 31;
    const [page, setPage] = useState(0);
    const [selectedPlacement, setSelectedPlacement] = useState<string>('전체');
    
    // 테이블 ref - 엑셀 다운로드용
    const tableRef = useRef<HTMLTableElement>(null);

    // 지면 목록 추출
    const placementList = useMemo(() => {
      const placements = new Set<string>();
      dailyStats.forEach(stat => {
        if (stat.placementGroup) {
          placements.add(stat.placementGroup);
        }
      });
      return Array.from(placements).sort();
    }, [dailyStats]);

    // 선택된 지면으로 필터링
    const filteredStats = useMemo(() => {
      if (selectedPlacement === '전체') {
        return dailyStats;
      }
      return dailyStats.filter(stat => stat.placementGroup === selectedPlacement);
    }, [dailyStats, selectedPlacement]);

    // 날짜별로 그룹화하고 미디에이션별로 분류
    const dateSummariesRaw = useMemo(() => {
      const grouped = filteredStats.reduce((acc, item) => {
        if (!acc[item.date]) {
          acc[item.date] = {
            date: item.date,
            totalRevenue: 0,
            totalReq: 0,
            totalRes: 0,
            totalImp: 0,
            totalClk: 0,
            mediations: []
          };
        }
        acc[item.date].totalRevenue += item.revenue;
        acc[item.date].totalReq += item.req;
        acc[item.date].totalRes += item.res;
        acc[item.date].totalImp += item.imp;
        acc[item.date].totalClk += item.clk;
        acc[item.date].mediations.push(item);
        return acc;
      }, {} as Record<string, DateSummary>);

      return Object.values(grouped);
    }, [filteredStats]);

    const { sorted: dateSummaries, sortDirection, toggleSort } = useDateSort(dateSummariesRaw);

    // 페이지네이션된 데이터
    const paginatedSummaries = useMemo(() => {
      const startIndex = page * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      return dateSummaries.slice(startIndex, endIndex);
    }, [dateSummaries, page, rowsPerPage]);

    const formatCurrency = useCallback((value: number) => {
      return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(value);
    }, []);

    const formatNumberOnly = useCallback((value: number) => Math.round(value).toLocaleString('ko-KR'), []);

    const calculateFillRate = (req: number, res: number) => {
      return req > 0 ? ((res / req) * 100).toFixed(2) : '0.00';
    };

    const calculateCTR = (imp: number, clk: number) => {
      return imp > 0 ? ((clk / imp) * 100).toFixed(2) : '0.00';
    };

    const calculateECPM = useCallback((revenue: number, imp: number) => {
      return imp > 0 ? formatNumberOnly((revenue / imp) * 1000) : '0';
    }, [formatNumberOnly]);

    // 엑셀 다운로드용 전체 데이터 (날짜별 요약만, 현재 정렬 상태 그대로)
    // 퍼센트 컬럼은 소수로 전달
    const allRowsDataForExcel = useMemo(() => {
      return dateSummaries.map((summary) => {
        // fillRate와 CTR을 소수 형태로 (0.xx)
        const totalFillRate = summary.totalReq > 0 ? summary.totalRes / summary.totalReq : 0;
        const totalCTR = summary.totalImp > 0 ? summary.totalClk / summary.totalImp : 0;
        const totalECPM = calculateECPM(summary.totalRevenue * exchangeRate, summary.totalImp);
        return [
          summary.date,
          formatNumberOnly(summary.totalRevenue * exchangeRate),
          summary.totalReq.toLocaleString(),
          summary.totalRes.toLocaleString(),
          totalFillRate,
          summary.totalImp.toLocaleString(),
          summary.totalClk.toLocaleString(),
          totalCTR,
          totalECPM
        ];
      });
    }, [dateSummaries, exchangeRate, formatNumberOnly, calculateECPM]);

    return (
      <Box>
        {/* 지면 선택 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mt: 2
          }}
        >
          <Typography variant="body1" fontWeight="bold">
            지면 선택
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              border: '1px solid #e0e0e0',
              borderRadius: 2,
              bgcolor: '#f5f5f5',
              minWidth: 300,
              maxWidth: 400
            }}
          >
            <SearchIcon sx={{ color: '#666', mr: 1 }} />
            <FormControl sx={{ flex: 1 }} variant="standard">
              <Select
                value={selectedPlacement}
                onChange={(e) => setSelectedPlacement(e.target.value)}
                displayEmpty
                renderValue={(value) => value || '전체'}
                disableUnderline
                IconComponent={KeyboardArrowDownIcon}
                inputProps={{
                  'aria-label': '지면 선택'
                }}
                sx={{
                  '& .MuiSelect-select': {
                    padding: 0,
                    paddingRight: '24px !important',
                    display: 'flex',
                    alignItems: 'center'
                  },
                  '& .MuiSelect-icon': {
                    color: '#666'
                  }
                }}
              >
                <MenuItem value="전체">전체</MenuItem>
                {placementList.map((placement) => (
                  <MenuItem key={placement} value={placement}>
                    {placement}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* 미디에이션별 매출 차트 */}
        <AdcashAvatyeDetailMetricsChart
          data={data}
          selectedPlacement={selectedPlacement}
          selectedApp={selectedApp}
          exchangeRate={exchangeRate}
        />
        {/* 환율 버튼 1230 or 1350 */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mt: 2
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[1230, 1350].map(rate => (
              <Button
                key={rate}
                size="small"
                variant="outlined"
                onClick={() => onExchangeRateChange(rate)}
                sx={{
                  borderColor: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                  color: exchangeRate === rate ? '#0044D2' : '#9e9e9e',
                  fontWeight: 'bold',
                  '&:hover': {
                    borderColor: exchangeRate === rate ? '#0044D2' : '#757575',
                    backgroundColor: exchangeRate === rate ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                  }
                }}
              >
                환율 {rate}
              </Button>
            ))}
          </Box>
          <TableActionsMenu tableRef={tableRef} allRowsData={allRowsDataForExcel} />
        </Box>
        
        <TableContainer
          sx={{
            overflowX: 'auto',
            mt: 2
          }}
        >
          <Table
            ref={tableRef}
            size="small"
            sx={{
              borderCollapse: 'collapse',
              width: '100%'
            }}
          >
            <TableHead>
              <TableRow sx={{ height: 36 }}>
                <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>
                  <TableSortLabel
                    active
                    direction={sortDirection}
                    onClick={toggleSort}
                  >
                    날짜
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>총 매출</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>request</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>response</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>fill rate</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>impression</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>click</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>ctr</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>eCPM</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedSummaries.map(summary => {
                const dateKey = summary.date;
                const totalFillRate = calculateFillRate(summary.totalReq, summary.totalRes);
                const totalCTR = calculateCTR(summary.totalImp, summary.totalClk);
                const totalECPM = calculateECPM(summary.totalRevenue * exchangeRate, summary.totalImp);

                // 미디에이션별로 그룹화
                const mediationGroups: Record<string, AdcashAvatyeDetailMetricsByPlacementWithMediationDailyStats[]> = {};
                summary.mediations.forEach(item => {
                  if (!mediationGroups[item.mediation]) {
                    mediationGroups[item.mediation] = [];
                  }
                  mediationGroups[item.mediation].push(item);
                });

                return (
                  <Fragment key={dateKey}>
                    {/* 날짜 요약 행 */}
                    <TableRow 
                      sx={{ 
                        backgroundColor: '#fff',
                        height: 32
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px' }}>{summary.date}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {formatCurrency(summary.totalRevenue * exchangeRate)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalReq.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalRes.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {totalFillRate}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalImp.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {summary.totalClk.toLocaleString()}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {totalCTR}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', px: '6px', color: 'gray' }}>
                        {totalECPM}
                      </TableCell>
                    </TableRow>

                    {/* 미디에이션별 상세 데이터 */}
                    {Object.entries(mediationGroups)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([mediation, items]) => {
                        // 같은 미디에이션의 데이터 합산
                        const mediationTotal = items.reduce((acc, item) => ({
                          revenue: acc.revenue + item.revenue,
                          req: acc.req + item.req,
                          res: acc.res + item.res,
                          imp: acc.imp + item.imp,
                          clk: acc.clk + item.clk
                        }), { revenue: 0, req: 0, res: 0, imp: 0, clk: 0 });

                        const fillRate = calculateFillRate(mediationTotal.req, mediationTotal.res);
                        const ctr = calculateCTR(mediationTotal.imp, mediationTotal.clk);
                        const ecpm = calculateECPM(mediationTotal.revenue * exchangeRate, mediationTotal.imp);

                        return (
                          <TableRow 
                            key={`${dateKey}-${mediation}`}
                            sx={{ 
                              backgroundColor: '#fafafa',
                              height: 32
                            }}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap', pl: 4, px: '6px' }}>{mediation}</TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {formatCurrency(mediationTotal.revenue * exchangeRate)}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {mediationTotal.req.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {mediationTotal.res.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {fillRate}%
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {mediationTotal.imp.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {mediationTotal.clk.toLocaleString()}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {ctr}%
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap', px: '6px' }}>
                              {ecpm}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={dateSummaries.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[rowsPerPage]}
        />
      </Box>
    );
  }

